import React, { useState } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { Badge } from "@amzn/awsui-components-react-v3";
import styles from "./permissions_page.css";
import { LoadingState } from "../http";
import helpPanelSlice, { DisplayHelpPanel } from "../help_panel/helpPanelSlice";
import { InheritedAccessDetails } from "../createTeam/inheritedAccessDetails";
import { MetricsState } from "../logging";
import { TeamDtoWithBreadcrumb } from "../layout/subheader/SubHeaderDto";
import RemoveUserModal, {
  RemoveUserResult,
} from "./removeUser/RemoveUserModal";
import useWorkflowAuthorizationCheck, {
  UseWorkflowAuthorizationHookResult,
} from "../common/authorization/workflowAuthorization";
import PermissionsTable from "./PermisisonsTable";
import { UserAccess } from "./UserAccess";
import bannerSlice, { DisplayBanner } from "../banner/bannerSlice";
import useGetTeamAccessDetails from "./hooks/use-get-team-access-details";
import { TeamAccessDetailsResponse } from "./api/get-team-access-details";

interface PermissionsPageProps {
  team: TeamDtoWithBreadcrumb;
  metrics: MetricsState;
}

interface PermissionsPageDispatchProps {
  setHelpPanelContent: (payload: DisplayHelpPanel | undefined) => void;
  setHelpPanelOpenState: (payload: boolean | true) => void;
  setBanner: (banner: DisplayBanner | undefined) => void;
}

const createAdminName = (
  userPopoverComponent: JSX.Element,
  userFullName: string,
  userLogin: string,
  isInheritedAccess: boolean,
  isTeamOwner: boolean
): JSX.Element => {
  return (
    <div className={styles["flex-style"]}>
      <span>
        {isInheritedAccess ? (
          <i>
            {userPopoverComponent}
            {` (${userLogin}@)`}
          </i>
        ) : (
          <>
            {userPopoverComponent}
            {` (${userLogin}@)`}
          </>
        )}
        {isTeamOwner && <Badge color="grey">Team owner</Badge>}
      </span>
    </div>
  );
};

const createAccess = (
  isInheritedAccess: boolean,
  teamName: string
): JSX.Element => {
  if (isInheritedAccess) {
    return (
      <div>
        <i>{`Admin (Inherited from ${teamName})`}</i>
      </div>
    );
  }
  return <div>Admin</div>;
};

const PermissionsPage: React.FC<PermissionsPageProps &
  PermissionsPageDispatchProps> = ({
  setHelpPanelContent,
  setHelpPanelOpenState,
  team,
  metrics,
  setBanner,
}): JSX.Element => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [localChanges, setLocalChanges] = useState<Partial<RemoveUserResult>>(
    {}
  );

  const {
    data: inheritedAccessResponse,
    isLoading: inheritedAccessIsLoading,
    refetch,
  } = useGetTeamAccessDetails(team.teamId);

  const inheritedAccessLoadingState = inheritedAccessIsLoading
    ? LoadingState.LOADING
    : LoadingState.RESOLVED;

  const {
    loadingState: isAuthorizedLoadingState,
    isAuthorized,
  }: UseWorkflowAuthorizationHookResult = useWorkflowAuthorizationCheck(
    team.teamId,
    metrics
  );

  const createAction = (item: UserAccess): JSX.Element => {
    const { isInheritedAccess, isTeamOwner } = item;
    if (!isInheritedAccess && !isTeamOwner && isAuthorized) {
      return (
        <div className={styles["action-cell"]}>
          <a
            onClick={() => {
              setBanner(undefined);
              setLocalChanges({ user: item, teamId: team.teamId });
              setShowModal(true);
            }}
            rel="noopener noreferrer"
          >
            Remove
          </a>
        </div>
      );
    }
    return (
      <div className={`${styles["remove-text"]} ${styles["action-cell"]}`}>
        Remove
      </div>
    );
  };

  const columnDefinitions = [
    {
      id: "name",
      header: "Name",
      cell: (item: UserAccess) =>
        createAdminName(
          item.userPopoverComponent,
          item.userFullName,
          item.userLogin,
          item.isInheritedAccess,
          item.isTeamOwner
        ),
    },
    {
      id: "accessType",
      header: "Access type",
      cell: (item: UserAccess) =>
        createAccess(item.isInheritedAccess, item.teamName),
    },
    {
      id: "actions",
      header: <div className={styles["action-cell"]}>Actions</div>,
      cell: (item: UserAccess) => createAction(item),
    },
  ];

  function getPermissionsDetails(
    inheritedAccessDetails: InheritedAccessDetails[]
  ): JSX.Element {
    const isLoading =
      isAuthorizedLoadingState === LoadingState.LOADING ||
      inheritedAccessLoadingState === LoadingState.LOADING;
    return (
      <PermissionsTable
        inheritedAccessDetails={inheritedAccessDetails}
        team={team}
        metrics={metrics}
        setHelpPanelContent={setHelpPanelContent}
        setHelpPanelOpenState={setHelpPanelOpenState}
        columnDefinitions={columnDefinitions}
        isAuthorized={isAuthorized}
        isLoading={isLoading}
        reloadPermissionsTable={refetch}
      />
    );
  }

  /**
   * Determines what to show based on the loading state of the inherited
   * access request.
   * @param inheritedAccessDetails The inherited access details (supplied by the access request).
   * @param inheritedAccessLoading The loading state of the inherited access request.
   * @param team The team for which we are getting inherited access details.
   * @param setHelpPanelContent Callback to set the help panel content.
   * @param setHelpPanelOpenState Callback to set the help panel open state.
   */
  function permissionsTableDisplay(
    inheritedAccessDetails: TeamAccessDetailsResponse | undefined
  ): JSX.Element {
    const inheritedAccessData = inheritedAccessDetails
      ? inheritedAccessDetails.data
      : [];
    return getPermissionsDetails(inheritedAccessData);
  }

  return (
    <React.Fragment>
      {showModal && (
        <RemoveUserModal
          metrics={metrics}
          item={localChanges}
          setShowModal={setShowModal}
          showModal={showModal}
          reloadPermissionsTable={refetch}
          setBanner={setBanner}
        />
      )}
      {permissionsTableDisplay(inheritedAccessResponse)}
    </React.Fragment>
  );
};

const mapDispatchToProps = (
  dispatch: Dispatch
): PermissionsPageDispatchProps => ({
  setHelpPanelContent(helpPanelContent: DisplayHelpPanel | undefined) {
    dispatch(helpPanelSlice.actions.setHelpPanelContent(helpPanelContent));
  },
  setHelpPanelOpenState(helpPanelState: boolean) {
    dispatch(helpPanelSlice.actions.setHelpPanelOpenState(helpPanelState));
  },
  setBanner: (banner: DisplayBanner | undefined) => {
    dispatch(bannerSlice.actions.setBanner(banner));
  },
});

export default connect(null, mapDispatchToProps)(PermissionsPage);
